import {
  pageOffset,
  PayrollQueueQuery,
  type PayrollQueue,
  type PayrollRow,
} from '@myrewards/contracts';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useId, useRef, useState, type ReactElement, type RefObject } from 'react';
import { Button as AriaButton, Link } from 'react-aria-components';
import { meQuery } from '../../api';
import { payLine, payrollKeys, payrollQuery, payrollListQuery } from '../../api/close';
import { useSearchParams } from 'react-router';
import { ManageTabs } from '../../manage/ManageTabs';
import { isStale } from '../../ops/labels';
import { AmountInput, Notice } from '../../ops/shared';
import { Hero, PageBody } from '../../shell';
import { StatementCell } from './StatementCell';
import p from './Payroll.module.css';
import {
  Button,
  Card,
  ConfirmDialog,
  cx,
  EmptyState,
  formatCount,
  formatMoney,
  formatShortDate,
  Icon,
  StatusPill,
  tableCards,
  useCardRows,
  useScrollRegion,
  useToast,
  Pager,
  pagerWaiting,
  Select,
  TextField,
  usePaging,
  useHelpAnchor,
  HelpShownTag,
  type TableCards,
  useClampedPage,
} from '../../ui';
import { closeProblemWords, netWords, paysAsWords } from '../labels';
import {
  choiceWords,
  codingWords,
  estimateSource,
  finalTaxProblem,
  netBeforePay,
  payDateWords,
  hasSpotSection,
  otherCounts,
  queueSentence,
  spotTotalWords,
} from './presenter';
import o from '../../ops/Ops.module.css';
import s from '../Close.module.css';

const COLUMNS = 7;

/** Each to-pay row's final tax as Payroll types it: starts at the estimate. */
type Drafts = Record<string, number | null>;

function LineDetails({ row, id }: { row: PayrollRow; id: string }): ReactElement {
  const source = estimateSource(row);
  const choice = choiceWords(row);
  return (
    <div id={id} className={s.details}>
      <div className={s.detailBox}>
        <p className={cx('eyebrow', s.detailLabel)}>Charge code · read-only</p>
        <p className={s.detailTitle}>{codingWords(row)}</p>
        <p className={o.sub}>
          {row.award.displayId} · charge codes come from the award and never change here.
        </p>
      </div>
      <div className={s.detailBox}>
        <p className={cx('eyebrow', s.detailLabel)}>Estimate · where it came from</p>
        <p className={s.detailTitle}>{source.title}</p>
        <p className={o.sub}>{source.detail}</p>
      </div>
      <div className={s.detailBox}>
        <p className={cx('eyebrow', s.detailLabel)}>{row.person.name} · the choice</p>
        <p className={s.detailTitle}>{choice.title}</p>
        <p className={o.sub}>{choice.detail}</p>
      </div>
    </div>
  );
}

function PayrollLine({
  row,
  draft,
  onDraft,
  completed,
  timeZone,
  onPay,
  onUploaded,
  problem,
  c,
}: {
  row: PayrollRow;
  draft: number | null;
  onDraft: (cents: number | null) => void;
  completed: boolean;
  timeZone: string;
  onPay: () => void;
  onUploaded: () => void;
  problem: string | null;
  /** Card rows below 720 px (QA-030). */
  c: TableCards;
}): ReactElement {
  const [open, setOpen] = useState(false);
  // Mark paid was pressed with no usable tax: say why at the box (it's never hard-disabled, so a
  // tax typed and clicked straight away pays in one click).
  const [attempted, setAttempted] = useState(false);
  const taxRef = useRef<HTMLInputElement>(null);
  const detailsId = useId();
  const problemId = useId();
  const name = row.person.name;
  const pays = paysAsWords(row.payAs, row.type);
  const toPay = row.status === 'to_pay';
  // A System Admin sees what's to pay without paying it (APP-134): no tax box, no Mark paid.
  const paying = toPay && row.canPay;
  const net = toPay ? netBeforePay(row, draft) : (row.final ?? row.estimate);
  const estimateTax = row.estimate?.taxCents ?? null;
  const payDate = payDateWords(row);
  const taxProblem = toPay ? finalTaxProblem(row, draft) : null;
  const showProblem = taxProblem !== null && (attempted || draft !== estimateTax);
  return (
    <>
      <tr className={cx(open && s.openRow)} {...c.row}>
        {/* On a phone the toggle says what it does, not a bare chevron under a label (APP-067 §13). */}
        <td {...c.cell('', 'value', c.on ? undefined : s.expander)}>
          <AriaButton
            className={cx(s.expandButton, c.on && s.expandText)}
            aria-expanded={open}
            aria-controls={detailsId}
            aria-label={`${open ? 'Hide' : 'Show'} details for ${name}`}
            onPress={() => {
              setOpen((v) => !v);
            }}
          >
            {c.on && <span>{open ? 'Hide details' : 'Show details'}</span>}
            <Icon name={open ? 'chevronUp' : 'chevronDown'} size={18} />
          </AriaButton>
        </td>
        <td {...c.cell('Line', 'title')}>
          <Link href={`/awards/${row.award.id}`} className={cx(o.strongLink, s.nowrap)}>
            {row.award.displayId}
          </Link>
          <span className={o.sub}>
            <span className={o.strong}>{name}</span>
            {row.person.eeNumber !== null && ` · EE# ${row.person.eeNumber}`}
          </span>
          <span className={o.sub}>{pays.label}</span>
          {/* APP-248 (S8): a Spot Award reaches Payroll when its letter is released. */}
          {row.type === 'SPOT' && row.releasedAt !== null && (
            <span className={o.sub}>Letter released {formatShortDate(row.releasedAt)}</span>
          )}
        </td>
        <td {...c.cell('Cash', 'value', s.num)}>{formatMoney(row.grossCashCents)}</td>
        <td {...c.cell('Final tax', 'value', s.num)}>
          {toPay && !paying ? (
            <>
              <span className={s.cellStrong}>Set when Payroll pays</span>
              {estimateTax !== null && (
                <span className={o.sub}>Estimate {formatMoney(estimateTax)}</span>
              )}
            </>
          ) : toPay ? (
            <>
              <AmountInput
                label={`Final tax for ${name}`}
                valueCents={draft}
                onChange={(cents) => {
                  setAttempted(false);
                  onDraft(cents);
                }}
                wholeDollars={false}
                maxCents={row.grossCashCents}
                isInvalid={showProblem}
                {...(showProblem && { describedBy: problemId })}
                inputRef={taxRef}
              />
              {estimateTax !== null && (
                <span className={o.sub}>Estimate {formatMoney(estimateTax)}</span>
              )}
              {showProblem && (
                <span id={problemId} role="alert" className={s.fieldProblem}>
                  {taxProblem}
                </span>
              )}
            </>
          ) : (
            <span className={s.cellStrong}>{formatMoney(row.final?.taxCents ?? 0)}</span>
          )}
        </td>
        <td {...c.cell('Net to employee', 'value', s.num)}>
          {net === null ? 'After the final tax' : netWords(net)}
          <span className={o.sub}>
            {toPay
              ? row.payAs === 'cash' || row.payAs === 'a' || row.payAs === 'closed_as_cash'
                ? 'Estimate'
                : 'Estimate until you set the final tax'
              : 'Paid'}
          </span>
        </td>
        <td {...c.cell('Earning statement')}>
          <StatementCell row={row} onUploaded={onUploaded} />
        </td>
        {completed && (
          <td {...c.cell('Completed', 'value')}>
            {row.completedAt === null
              ? 'Completed'
              : new Intl.DateTimeFormat('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  timeZone,
                }).format(new Date(row.completedAt))}
          </td>
        )}
        <td {...c.cell('Payment', paying ? 'action' : 'value')}>
          <div className={p.payment}>
            {toPay && !paying ? (
              <StatusPill size="sm" tone="waiting" label="To pay" />
            ) : toPay ? (
              <div className={s.actionCell}>
                <Button
                  size="sm"
                  onPress={() => {
                    if (taxProblem === null) {
                      onPay();
                      return;
                    }
                    setAttempted(true);
                    taxRef.current?.focus();
                  }}
                  aria-label={`Mark paid for ${name}`}
                >
                  Mark paid
                </Button>
                {problem !== null && <Notice tone="problem">{problem}</Notice>}
              </div>
            ) : (
              <StatusPill size="sm" tone="done" label="Paid" />
            )}
            <span className={o.sub}>
              {payDate.text}
              {payDate.sub !== null && (
                <>
                  <br />
                  {payDate.sub}
                </>
              )}
            </span>
          </div>
        </td>
      </tr>
      <tr className={s.detailRow} hidden={!open} {...c.row}>
        <td colSpan={COLUMNS + (completed ? 1 : 0)} {...c.wide}>
          <LineDetails row={row} id={detailsId} />
        </td>
      </tr>
    </>
  );
}

function LinesTable({
  rows,
  completed,
  timeZone,
  caption = 'Lines',
}: {
  rows: readonly PayrollRow[];
  completed: boolean;
  timeZone: string;
  /** The table's and its scroll region's name: one of its own for each section (APP-248). */
  caption?: string;
}): ReactElement {
  const toast = useToast();
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const region = useScrollRegion(scrollRef, caption);
  const [drafts, setDrafts] = useState<Drafts>({});
  const [paying, setPaying] = useState<PayrollRow | null>(null);
  const [problems, setProblems] = useState<Record<string, string>>({});

  const draftOf = (row: PayrollRow): number | null =>
    row.lineId in drafts ? (drafts[row.lineId] ?? null) : (row.estimate?.taxCents ?? null);

  const refresh = async (): Promise<void> => {
    // Every line on an award shares its version: read the whole queue again.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: payrollKeys.queue }),
      queryClient.invalidateQueries({ queryKey: ['home', 'waiting'] }),
      queryClient.invalidateQueries({ queryKey: ['awards'] }),
      queryClient.invalidateQueries({ queryKey: ['statements'] }),
    ]);
  };

  const pay = useMutation({
    mutationFn: ({ row, tax }: { row: PayrollRow; tax: number }) =>
      payLine(row.lineId, { version: row.version, finalTaxCents: tax }),
    onSuccess: async (paid) => {
      setProblems((p) =>
        Object.fromEntries(Object.entries(p).filter(([id]) => id !== paid.lineId)),
      );
      await refresh();
      toast.show({
        tone: 'done',
        title: `${paid.person.name} paid`,
        body: `${paid.award.displayId} · final tax ${formatMoney(paid.final?.taxCents ?? 0)}. ${paid.status === 'done' ? 'Payment and statement are complete.' : 'Attach the earning statement next.'}`,
      });
    },
    onError: async (error, { row }) => {
      setProblems((p) => ({ ...p, [row.lineId]: closeProblemWords(error) }));
      if (isStale(error)) await refresh();
    },
  });

  const c = tableCards(useCardRows());
  const payingTax = paying === null ? null : draftOf(paying);
  const payingNet = paying === null ? null : netBeforePay(paying, payingTax);

  return (
    <>
      <div ref={scrollRef} className={s.tableScroll} {...(c.on ? {} : region)}>
        <table {...c.table(s.table)}>
          <caption className="visually-hidden">{caption}</caption>
          <thead {...c.head}>
            <tr {...c.row}>
              <th scope="col" {...c.header}>
                <span className="visually-hidden">Details</span>
              </th>
              <th scope="col" {...c.header}>
                Line
              </th>
              <th scope="col" className={s.num} {...c.header}>
                Cash
              </th>
              <th scope="col" className={s.num} {...c.header}>
                Final tax
              </th>
              <th scope="col" className={s.num} {...c.header}>
                Net to employee
              </th>
              <th scope="col" {...c.header}>
                Earning statement
              </th>
              {completed && (
                <th scope="col" {...c.header}>
                  Completed
                </th>
              )}
              <th scope="col" {...c.header}>
                Payment
              </th>
            </tr>
          </thead>
          {rows.length === 0 && (
            <tbody {...c.body}>
              <tr className={s.emptyRow} {...c.row}>
                <td colSpan={COLUMNS + (completed ? 1 : 0)} {...c.wide}>
                  <EmptyState
                    variant="first-day"
                    art="calendar"
                    size="sm"
                    headline={
                      completed ? 'No completed records in this period' : 'Nothing to pay yet'
                    }
                    body={
                      completed
                        ? 'Choose another period or a custom date range to find an earlier record.'
                        : 'Cash awards arrive here when they are ready for payment. Paid lines stay here until their earning statement is ready.'
                    }
                  />
                </td>
              </tr>
            </tbody>
          )}
          <tbody {...c.body}>
            {rows.map((row) => (
              <PayrollLine
                key={row.lineId}
                row={row}
                completed={completed}
                timeZone={timeZone}
                draft={draftOf(row)}
                onDraft={(cents) => {
                  setDrafts((d) => ({ ...d, [row.lineId]: cents }));
                }}
                onPay={() => {
                  setPaying(row);
                }}
                onUploaded={() => {
                  void refresh();
                }}
                problem={problems[row.lineId] ?? null}
                c={c}
              />
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        isOpen={paying !== null}
        onOpenChange={(open) => {
          if (!open) setPaying(null);
        }}
        title={paying === null ? 'Mark paid?' : `Mark ${paying.person.name} paid?`}
        intro={
          paying?.type === 'SPOT'
            ? 'The final tax is recorded and the cash paid works out from it. A member of the Spot Committee checks the earning statement next.'
            : paying !== null && paying.payAs !== 'cash' && paying.payAs !== 'a'
              ? 'The final tax is recorded and the elected stock is bought with what’s left, in whole shares. The Stock Admin team checks the earning statement next.'
              : 'The final tax is recorded and the cash paid works out from it. The Stock Admin team checks the earning statement next.'
        }
        summary={
          paying === null
            ? []
            : [
                { label: 'Line', value: `${paying.award.displayId} · ${paying.person.name}` },
                { label: 'Cash', value: formatMoney(paying.grossCashCents) },
                {
                  label: 'Final tax',
                  ...(paying.estimate === null
                    ? {}
                    : { before: formatMoney(paying.estimate.taxCents) }),
                  value: formatMoney(payingTax ?? 0),
                },
                {
                  label: 'Net to employee (estimate)',
                  // Never a blank value: until a final tax is typed, the net follows from it.
                  value: payingNet === null ? 'After the final tax' : netWords(payingNet),
                },
              ]
        }
        withoutReason
        confirmLabel="Mark paid"
        onConfirm={async () => {
          if (paying === null || payingTax === null) return;
          await pay.mutateAsync({ row: paying, tax: payingTax }).catch(() => undefined);
        }}
      />
    </>
  );
}

const PERIODS = [
  { id: 'last_day', label: 'Last day' },
  { id: 'last_30_days', label: 'Last 30 days' },
  { id: 'last_90_days', label: 'Last 90 days' },
  { id: 'this_year', label: 'This year' },
  { id: 'all_time', label: 'All time' },
  { id: 'custom', label: 'Custom range' },
];

/** A committed range keys this editor, so browser history restores both date fields together. */
function CustomDateRange({
  initialFrom,
  initialTo,
  onApply,
}: {
  initialFrom: string;
  initialTo: string;
  onApply: (values: Record<string, string>) => void;
}): ReactElement {
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <TextField label="From" value={from} onChange={setFrom} placeholder="YYYY-MM-DD" />
      <TextField label="To" value={to} onChange={setTo} placeholder="YYYY-MM-DD" />
      <Button
        variant="ghost"
        onPress={() => {
          const values = { view: 'completed', period: 'custom', from, to };
          if (!PayrollQueueQuery.safeParse(values).success) {
            setError('Enter valid dates in YYYY-MM-DD format, with From on or before To.');
            return;
          }
          setError(null);
          onApply(values);
        }}
      >
        Apply dates
      </Button>
      {error !== null && <Notice tone="problem">{error}</Notice>}
    </>
  );
}

/** The view a cached list was asked for (the bare key is the In progress list). */
function viewOfKey(key: readonly unknown[]): string {
  const last = key.at(-1);
  return typeof last === 'object' &&
    last !== null &&
    'view' in last &&
    typeof last.view === 'string'
    ? last.view
    : 'in_progress';
}

/**
 * APP-248 (S8, change 22): the Spot Awards in progress, in a section of their own above the other
 * awards. The same lines Payroll already has, each paid as any other (final tax, Mark paid, the
 * statement), with the total still to pay. Its own pager, on `spotPage`/`spotSize`.
 */
function SpotSection({ summary }: { summary: PayrollQueue['spot'] }): ReactElement {
  const paging = usePaging('payroll', { param: 'spotPage', sizeParam: 'spotSize' });
  const { page, size, ready, anchorRef } = paging;
  const result = useQuery({
    ...payrollListQuery({ view: 'in_progress', group: 'spot', page, size }),
    enabled: ready,
    placeholderData: keepPreviousData,
  });
  const list = result.data;
  useClampedPage(paging, result.isPlaceholderData ? undefined : list?.page.number);
  const total = spotTotalWords(summary);
  return (
    <Card
      title="Spot awards ready to pay"
      aside={<span className={o.filterNote}>{queueSentence(summary)}</span>}
      help={{
        shows:
          'Spot Awards to pay, and those paid with an earning statement still to attach. Each arrives when its letter is released. The total still to pay names the pools it comes from.',
        canDo:
          'Set the final tax and mark a person paid, as for any award. Attach or replace an earning statement where the record allows it.',
      }}
    >
      {result.isError ? (
        <Notice tone="problem">
          {closeProblemWords(result.error)}{' '}
          <Button
            variant="ghost"
            onPress={() => {
              void result.refetch();
            }}
          >
            Try again
          </Button>
        </Notice>
      ) : list === undefined ? (
        <p role="status">Loading Spot awards…</p>
      ) : (
        <div ref={anchorRef as RefObject<HTMLDivElement>} tabIndex={-1}>
          <LinesTable
            rows={list.rows}
            completed={false}
            timeZone={list.timeZone}
            caption="Spot award lines"
          />
        </div>
      )}
      {total !== null && <p className={p.spotTotal}>{total}</p>}
      {list !== undefined && (
        <Pager
          label="Spot awards ready to pay"
          page={list.page}
          {...pagerWaiting(paging, result.isPlaceholderData)}
          onPage={(n) => {
            paging.setPage(n);
          }}
          onSize={(next) => {
            paging.setSize(next, pageOffset(list.page));
          }}
        />
      )}
    </Card>
  );
}

/** Payroll work is a separate section under the person's Home (APP-117). */
export function PayrollPage(): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const { data: active } = useSuspenseQuery(payrollQuery);
  const [params, setParams] = useSearchParams();
  // Page and size belong to the pager (usePaging); a bad one there never spoils the filters.
  const address = Object.fromEntries(params);
  delete address.page;
  delete address.size;
  // APP-248: the Spot section's pager, and the group, which the page decides, not the address.
  delete address.spotPage;
  delete address.spotSize;
  delete address.group;
  const parsed = PayrollQueueQuery.safeParse(address);
  const filters = parsed.success ? parsed.data : PayrollQueueQuery.parse({});
  const completed = filters.view === 'completed';
  // APP-248 (S8): Spot Awards in progress get a section of their own, the rest stay below it.
  const spotSection = !completed && hasSpotSection(active);
  const paging = usePaging('payroll');
  const { page, size, ready, anchorRef } = paging;
  const query = {
    ...filters,
    group: spotSection ? ('other' as const) : ('all' as const),
    page,
    size,
  };
  const result = useQuery({
    ...payrollListQuery(query),
    enabled: ready,
    // Hold the rows being read while the next page loads, but not across a change of view.
    placeholderData: (previous, previousQuery) =>
      previousQuery !== undefined && viewOfKey(previousQuery.queryKey) === query.view
        ? keepPreviousData(previous)
        : undefined,
  });
  const codesAnchor = useHelpAnchor('payroll-codes');
  const queue = result.data;
  // A page past the end comes back clamped: put the address on the page that was sent.
  useClampedPage(paging, result.isPlaceholderData ? undefined : queue?.page.number);
  // A new view or period starts at page 1; the rows per page carry over.
  const choose = (values: Record<string, string>) => {
    const size = params.get('size');
    setParams(size === null ? values : { ...values, size });
  };
  const changePeriod = (period: string | null) => {
    if (period === null) return;
    choose({
      view: 'completed',
      period,
      ...(period === 'custom' ? { from: active.asOf, to: active.asOf } : {}),
    });
  };
  return (
    <>
      <Hero
        eyebrow={`Payroll · ${me.person.greetingName}`}
        title="Payroll queue"
        documentTitle="Payroll queue"
        sub="Record payment and attach an earning statement for each person. Completed records stay available in your history."
        stats={[
          {
            label: 'Your queue · lines to pay',
            value: formatCount(active.counts.toPay),
            meta: 'Ready for payroll',
          },
          {
            label: 'Your queue · statements to attach',
            value: formatCount(active.counts.statementsDue),
            meta: 'Paid · awaiting a usable statement',
          },
        ]}
        titleTestId="payroll-title"
      />
      <ManageTabs current="payroll" />
      <PageBody>
        <div className={o.stack}>
          <div className={p.views} role="group" aria-label="Payroll views">
            <Button
              variant={completed ? 'ghost' : 'navy'}
              aria-pressed={!completed}
              onPress={() => {
                choose({});
              }}
            >
              In progress ({active.counts.toPay + active.counts.statementsDue})
            </Button>
            <Button
              variant={completed ? 'navy' : 'ghost'}
              aria-pressed={completed}
              onPress={() => {
                choose({ view: 'completed', period: 'last_30_days' });
              }}
            >
              Completed
            </Button>
          </div>
          {spotSection && <SpotSection summary={active.spot} />}
          <Card
            title={
              completed
                ? 'Completed payroll'
                : spotSection
                  ? 'Other awards ready to pay'
                  : 'In progress'
            }
            helpAnchor="payroll-lines"
            help={{
              shows: completed
                ? 'Payroll work completed in the selected period, by its original completion date.'
                : spotSection
                  ? 'Everyone else to pay or waiting on a usable earning statement, together in one list. Spot Awards are in their own section above.'
                  : 'Everyone to pay or waiting on a usable earning statement, together in one list.',
              canDo:
                'Set the final tax and mark a person paid. Attach or replace an earning statement where the record allows it. Open details for charge codes and estimates.',
            }}
            aside={
              <span className={o.filterNote}>
                {completed
                  ? 'Payment and statement work recorded'
                  : queueSentence(spotSection ? otherCounts(active) : active.counts)}
              </span>
            }
          >
            {completed && (
              <div className={p.filters}>
                <Select
                  label="Completed date"
                  value={filters.period}
                  options={PERIODS}
                  onChange={changePeriod}
                />
                {filters.period === 'custom' && (
                  <CustomDateRange
                    key={`${filters.from ?? active.asOf}/${filters.to ?? active.asOf}`}
                    initialFrom={filters.from ?? active.asOf}
                    initialTo={filters.to ?? active.asOf}
                    onApply={choose}
                  />
                )}
                <p>
                  Dates use {active.timeZone.replaceAll('_', ' ')}. Both custom dates are included.
                </p>
              </div>
            )}
            <p className={o.filterNote} {...codesAnchor.props}>
              Open a line’s details for its charge code, choice and tax estimate.{' '}
              <HelpShownTag shown={codesAnchor.shown} />
            </p>
            {!parsed.success && (
              <Notice tone="problem">
                The saved filters are invalid. Choose a view and date range again.
              </Notice>
            )}
            {result.isError ? (
              <Notice tone="problem">
                {closeProblemWords(result.error)}{' '}
                <Button
                  variant="ghost"
                  onPress={() => {
                    void result.refetch();
                  }}
                >
                  Try again
                </Button>
              </Notice>
            ) : queue === undefined ? (
              <p role="status">Loading payroll records…</p>
            ) : (
              <div ref={anchorRef as RefObject<HTMLDivElement>} tabIndex={-1}>
                <LinesTable rows={queue.rows} completed={completed} timeZone={queue.timeZone} />
              </div>
            )}
            {queue !== undefined && (
              <Pager
                // APP-236: "Payroll queue pages", so it never shares a name with Manage's
                // "Payroll pages" row above it.
                label="Payroll queue"
                page={queue.page}
                {...pagerWaiting(paging, result.isPlaceholderData)}
                onPage={(n) => {
                  paging.setPage(n);
                }}
                onSize={(size) => {
                  paging.setSize(size, pageOffset(queue.page));
                }}
              />
            )}
          </Card>
        </div>
      </PageBody>
    </>
  );
}
